import { useEffect, useState } from 'react';
import cloneDeep from 'lodash-es/cloneDeep';
import classNames from 'classnames/bind';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Styles from '../story-filters-styles.module.scss';
import ResetButtonInfo from '../StoryFiltersButton/ResetButtonInfo';
import FilterInner from './FilterInner';
import { getUserToken } from '../../../helpers/getUserToken';
import { resetSelectedFilters, togglePremiumLabels } from '../../../actions/storyFilters.action';
import FiltersMobileModal from '../FiltersMobileModal';
import StorageSvc from '../../../services/StorageSvc';
import { extraSourceValues } from '../../../data/filters/storyFilters';

const cx = classNames.bind(Styles);
const ResetButton = ({
  differentFiltersValues,
  onSimplifiedViewTabsClick,
  resetLanguages,
  resetSourceFilters,
  isLoading,
  piwikEnabled,
  hasBigScreenDesign,
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [selectStatus, setSelectStatus] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState(differentFiltersValues);
  const [openFilter, setOpenFilter] = useState(false);

  const width = useSelector((state) => state.common.width);
  const storyFilters = useSelector((state) => state.storyFilters);
  const dispatch = useDispatch();

  const openFilterModal = () => {
    if (!Object.keys(differentFiltersValues).length) {
      return;
    }

    setOpenFilter(!openFilter);
  };

  const toggleDropdown = (status, event, source) => {
    // workaround for IE11 to prevent double toggle when user clicks on
    // dropdown button to close dropdown
    if (
      (status === false
        && source.source === 'select')
      || dropdownOptions.length === 0
    ) {
      return;
    }

    if (width <= 1024) return openFilterModal();

    setDropdownOpen(status);
    if (!status) {
      setSelectAll(false);
      setDropdownOptions(dropdownOptions.map((option) => ({ ...option, select: false })));
      setSelectStatus(false);
    }
  };

  const handleCloseModal = () => {
    setOpenFilter(false);
    setSelectAll(false);
    setDropdownOptions(dropdownOptions.map((option) => ({ ...option, select: false })));
    setSelectStatus(false);
  };

  const selectAllCheckBox = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newOptions = cloneDeep(dropdownOptions);
    if (newOptions.length <= 0) {
      setSelectAll(false);
      return;
    }
    setSelectAll(!selectAll);
    const options = newOptions.map((option) => ({ ...option, select: !selectAll }));
    setDropdownOptions(options);
    if (!selectAll) {
      setSelectStatus(true);
    } else {
      setSelectStatus(false);
    }
  };

  const handleFilterChange = (value) => {
    const newOptions = cloneDeep(dropdownOptions);
    const foundIndex = newOptions.findIndex((option) => option.key === value);
    newOptions[foundIndex].select = !newOptions[foundIndex].select;
    const selectedCategories = newOptions.filter((f) => f.select);

    if (selectedCategories.length > 0) {
      setSelectStatus(true);
    } else {
      setSelectStatus(false);
    }

    setDropdownOptions(newOptions);
    if (selectedCategories.length === newOptions.length) {
      setSelectAll(true);
    } else if (selectAll && selectedCategories.length !== newOptions.length) {
      setSelectAll(false);
    }
  };

  const onCancel = () => {
    if (width <= 1024) {
      setOpenFilter(false);
    } else {
      setDropdownOpen(false);
    }
    setSelectAll(false);
    setDropdownOptions(dropdownOptions.map((option) => ({ ...option, select: false })));
    setSelectStatus(false);
  };

  const onReset = () => {
    let params = {};
    const newOptions = dropdownOptions.filter((option) => (option.select && option));
    newOptions.forEach((option) => {
      params = {
        ...params,
        [option.key]: option.key === 'sentiment_categories' ? `${[...option.resetValue]}` : option.resetValue,
      };
    });

    if (params.hasOwnProperty('languages')) {
      params.all_languages = false;
    }

    if (params.hasOwnProperty('categories') && !storyFilters.categories.includes('op')) {
      params.categories = params.categories.split(',').filter((cat) => cat !== 'op').join(',');
    }
    if (params.hasOwnProperty('major_sources_only')) {
      if (!params.hasOwnProperty('categories')) {
        params.categories = storyFilters.categories !== '' && storyFilters.categories.includes('mp') ? storyFilters.categories.concat(',', 'op') : storyFilters.categories;
      } else {
        params.categories = params.categories && params.categories.includes('mp') !== '' ? params.categories.concat(',', 'op') : params.categories;
      }
      StorageSvc.setItem(
        'ShowMajorSources',
        JSON.stringify({ status: false }),
      );
      resetSourceFilters.resetMajorContentFilter();
      delete params.major_sources_only;
    }

    if (params.hasOwnProperty('order_by')) {
      StorageSvc.setItem('selectedOrderTab',
        JSON.stringify({ activeOrderTab: params.order_by }));
    }
    if (params.hasOwnProperty('user_newsfeed_view_images_web')) {
      StorageSvc.setItem(
        'userNewsfeedViewImages',
        JSON.stringify({ status: true }),
      );
    }
    if (params.hasOwnProperty('show_similar_stories_in_feed')) {
      StorageSvc.setItem(
        'showSimilars',
        JSON.stringify({ status: true }),
      );
    }
    if (params.hasOwnProperty('group_similar_stories')) {
      StorageSvc.setItem(
        'groupSimilars',
        JSON.stringify({ status: true }),
      );
    }
    if (params.hasOwnProperty('expand_stories')) {
      StorageSvc.setItem(
        'expandStories',
        JSON.stringify({ status: false }),
      );
    }
    if (params.hasOwnProperty('user_newsfeed_view_description_web')) {
      StorageSvc.setItem(
        'userNewsfeedViewDescription',
        JSON.stringify({ status: false }),
      );
    }

    if (params.hasOwnProperty('user_newsfeed_view_web')) {
      StorageSvc.setItem(
        'userNewsfeedView',
        JSON.stringify({ status: 'rows' }),
      );
    }

    if (params.hasOwnProperty('user_newsfeed_compactview_web')) {
      StorageSvc.setItem(
        'userNewsfeedViewCompact',
        JSON.stringify({ status: false }),
      );
    }

    if (params.hasOwnProperty('selected_time_filter')) {
      params.time_filter = 'month1';
    }

    dispatch(resetSelectedFilters(getUserToken(), params));
    if (params.hasOwnProperty('user_newsfeed_advanced_view_web')) {
      onSimplifiedViewTabsClick?.(params.user_newsfeed_advanced_view_web);
      StorageSvc.setItem(
        'userSimplifiedView',
        params.user_newsfeed_advanced_view_web,
      );
    }
    if (params.hasOwnProperty('user_newsfeed_advanced_view')) {
      onSimplifiedViewTabsClick?.(params.user_newsfeed_advanced_view);
      StorageSvc.setItem(
        'userSimplifiedView',
        params.user_newsfeed_advanced_view,
      );
    }
    if (!getUserToken() && params.hasOwnProperty('languages')) {
      resetLanguages.reset();
    }
    if (params.hasOwnProperty('paywall')
      || params.hasOwnProperty('registrationRequired')) {
      resetSourceFilters.resetExtraValues(extraSourceValues);
    }

    if (params.hasOwnProperty('isPremiumLabelsEnabled')) {
      StorageSvc.setItem('showPremiumLabels', JSON.stringify({ status: params.isPremiumLabelsEnabled }));
      dispatch(togglePremiumLabels(params.isPremiumLabelsEnabled));
    }

    if (width <= 1024) {
      setOpenFilter(false);
    } else {
      setDropdownOpen(false);
    }
    setSelectAll(false);
    setSelectStatus(false);
    if (piwikEnabled) {
      _paq.push(['trackEvent', 'Story Filters', 'Reset']);
    }
  };

  useEffect(() => {
    if (differentFiltersValues.length === 0) {
      setDropdownOpen(false);
      setOpenFilter(false);
    }
    setDropdownOptions(differentFiltersValues.map((option) => ({ ...option, select: false })));
    if (differentFiltersValues.length <= 0) {
      setSelectAll(false);
      setSelectStatus(false);
    }
  }, [differentFiltersValues, dropdownOpen]);

  return (
    <Dropdown
      id={cx('sentiment-reset-filter-dropdown')}
      className={cx({
        open: dropdownOpen,
        disabled: dropdownOptions.length === 0,
        big_screen: hasBigScreenDesign,
      })}
      onToggle={toggleDropdown}
      show={dropdownOpen}
    >
      <Dropdown.Toggle>
        <ResetButtonInfo
          dropdownTitle={(
            <span className={Styles.sentiment_title}>
              Reset
            </span>
          )}
          hasBigScreenDesign={hasBigScreenDesign}
        />
      </Dropdown.Toggle>
      {dropdownOptions.length === 0 && (
        <div className={cx('tooltip')}>
          Default Settings -
          {' '}
          <br />
          Nothing to Reset.
        </div>
      )}
      {width >= 1025 ? (
        <Dropdown.Menu flip={false} bsPrefix="dropdown-menu">
          <FilterInner
            differentFiltersValues={dropdownOptions}
            selectAll={selectAllCheckBox}
            selectAllStatus={selectAll}
            handleFilterChange={handleFilterChange}
            selectStatus={selectStatus}
            cancel={onCancel}
            reset={onReset}
            isLoading={isLoading}
          />
        </Dropdown.Menu>
      )
        : (
          <FiltersMobileModal
            openFilter={openFilter}
            handleCloseModal={handleCloseModal}
            showCloseIcon
          >
            <FilterInner
              differentFiltersValues={dropdownOptions}
              selectAll={selectAllCheckBox}
              selectAllStatus={selectAll}
              handleFilterChange={handleFilterChange}
              selectStatus={selectStatus}
              cancel={onCancel}
              reset={onReset}
              isLoading={isLoading}
            />
          </FiltersMobileModal>
        )}
    </Dropdown>
  );
};
export default ResetButton;
